import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, CircularProgress, Button, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import Sidebar from "../../components/Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";
import { listTenantUsers, updateUser, createUser } from "../../services/accountsService";

import UsersHeader from "./UsersHeader";
import UsersList from "./UsersList";
import EditUserDialog from "./EditUserDialog";
import { useTranslation } from "react-i18next";

import { ROLES as ROLES_CONST } from "./roles";

const TenantUsersPage = () => {
  const { isLoggedIn, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  // Build translated roles map from const (keys match backend role values)
  const roles = useMemo(() => {
    return Object.fromEntries(
      Object.entries(ROLES_CONST).map(([roleKey, r]) => [
        roleKey,
        {
          ...r,
          // fallback label: roleKey if translation missing
          label: t(`${r.i18nKey}.label`, roleKey),
          description: t(`${r.i18nKey}.description`, ""),
        },
      ])
    );
  }, [t]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listTenantUsers({ ordering: "role,username" });
      if (!res.success) throw new Error(res.error || t("tenantUsers.errors.loadFailed", "Failed to load users"));
      setUsers(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (e) {
      enqueueSnackbar(e.message || t("tenantUsers.errors.loadFailed", "Failed to load users"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    if (isLoggedIn) loadUsers();
  }, [isLoggedIn, loadUsers]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadUsers();
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter((up) => {
      const u = up.user || {};
      return (
        (u.username || "").toLowerCase().includes(term) ||
        (u.email || "").toLowerCase().includes(term) ||
        (up.role || "").toLowerCase().includes(term) ||
        (u.first_name || "").toLowerCase().includes(term) ||
        (u.last_name || "").toLowerCase().includes(term)
      );
    });
  }, [users, q]);

  const openEdit = (up) => {
    setSelected(up);
    setEditOpen(true);
  };
  const openCreate = () => {
    setSelected(null);
    setEditOpen(true);
  };
  const closeEdit = () => setEditOpen(false);

  const saveUser = async (id, data, isCreate = false) => {
    const action = isCreate || !id ? "create" : "update";
    const fn = action === "create" ? createUser : updateUser;

    const res = action === "create" ? await fn(data) : await fn(id, data);
    if (!res.success) {
      throw new Error(
        res.error ||
        (action === "create"
          ? t("tenantUsers.errors.createFailed", "Create failed")
          : t("tenantUsers.errors.updateFailed", "Update failed"))
      );
    }

    enqueueSnackbar(
      action === "create"
        ? t("tenantUsers.snackbar.created", "User created")
        : t("tenantUsers.snackbar.updated", "User updated"),
      { variant: "success" }
    );

    await loadUsers();
  };

  if (!isLoggedIn) return <CircularProgress />;
  if (loading) return <CircularProgress />;

  return (
    <>
      <Sidebar user={user} />
      <Box display="flex" justifyContent="center" p={2}>
        <Paper sx={{ width: "100%", maxWidth: 960, p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={1}>
            <UsersHeader q={q} setQ={setQ} refreshing={refreshing} onRefresh={handleRefresh} />
            <Button variant="outlined" onClick={openCreate}>
              {t("tenantUsers.actions.newUser", "New user")}
            </Button>
          </Stack>

          <UsersList users={filtered} onEdit={openEdit} roles={roles} />
        </Paper>
      </Box>

      <EditUserDialog
        open={editOpen}
        onClose={closeEdit}
        userProfile={selected}
        onSave={async (id, data, mode) => {
          await saveUser(id, data, mode === "create");
          closeEdit();
        }}
        mode={selected ? "edit" : "create"}
        roles={roles}
      />
    </>
  );
};

export default TenantUsersPage;
